import type { RefObject } from 'react';
import { PaymentMethodEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';
import type { AppchargePaymentElementCard } from '@/types/appchargePaymentElement';
import { Errors } from '@/constants/errors';
import { ErrorLogs } from '@/constants/logs';
import { checkIfServerFinalizeFailure } from '@/utils/error';
import { buildAppchargePaymentElementCardRequest } from '@/utils/appchargePaymentElement';
import { logError } from '@/utils/logger';

class AppchargeCardGateError extends Error {
  constructor(
    readonly reason: string,
    readonly diagnostics: Record<string, unknown> = {}
  ) {
    super(Errors.payment.paymentFailed);
    this.name = 'AppchargeCardGateError';
  }
}

export const handleAppchargePaymentElementCardPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>
): Promise<unknown> => {
  try {
    const paymentMethodSettings = checkout.paymentMethodsSettings.find(
      (pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
    );

    if (!paymentMethodSettings) {
      throw new AppchargeCardGateError(Errors.appchargeCardFailureReason.missingPaymentMethodSettings);
    }

    const cardRef = checkout.card.cardElementRef as RefObject<AppchargePaymentElementCard> | null;
    const card = cardRef?.current;
    if (!card) {
      throw new AppchargeCardGateError(Errors.appchargeCardFailureReason.missingCardElement, {
        hasCardRef: Boolean(cardRef),
      });
    }

    const validation = await card.validate();
    if (!validation.valid) {
      throw new AppchargeCardGateError(Errors.appchargeCardFailureReason.validationFailed, {
        fields: summarizeFieldStates(validation.fields),
      });
    }

    const cardCollectResult = await card.collect();
    if (!cardCollectResult.ok) {
      throw new AppchargeCardGateError(Errors.appchargeCardFailureReason.collectFailed, {
        collectReason: cardCollectResult.reason,
        collectDetail: redactUnknownCollectDetail(cardCollectResult.detail),
      });
    }

    const request = buildAppchargePaymentElementCardRequest({
      cardCollectResult,
      paymentMethodSettings,
      email: checkout.customer.email ?? '',
      cardholderName: checkout.card.initialCardPayload?.cardholderName ?? '',
      shouldSaveCard: checkout.card.initialCardPayload?.isSaveCard ?? false,
    });

    return await sendPaymentToServer(request);
  } catch (error) {
    if (checkIfServerFinalizeFailure(error)) {
      throw error;
    }
    logAppchargeCardFailure(error);
    throw new Error(Errors.payment.paymentFailed);
  }
};

const logAppchargeCardFailure = (error: unknown): void => {
  const isGateFailure = error instanceof AppchargeCardGateError;
  logError(ErrorLogs.appchargePaymentElementPaymentFailed, isGateFailure ? undefined : error, {
    reason: isGateFailure ? error.reason : Errors.appchargeCardFailureReason.unexpectedError,
    ...(isGateFailure ? error.diagnostics : {}),
  });
};

const KNOWN_COLLECT_DETAILS: readonly string[] = ['destroyed'];

const redactUnknownCollectDetail = (detail?: string): string => {
  if (!detail) return 'none';
  return KNOWN_COLLECT_DETAILS.includes(detail) ? detail : 'unrecognized';
};

const summarizeFieldStates = (fields: Awaited<ReturnType<AppchargePaymentElementCard['validate']>>['fields']) =>
  Object.fromEntries(
    Object.entries(fields).map(([field, state]) => [
      field,
      state ? { isValid: state.isValid, isEmpty: state.isEmpty } : 'missing',
    ])
  );
